// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import type { ImageSourcePropType } from 'react-native';
import { buildMediaUrl } from '@/services/mediaUrl';
import { getAssetImageSource } from '@/services/assetImageSource';

const MEDIA_PAGE_PATH = '/tour/[tourId]/edit';

const GAME_IMAGE_MAP: Record<string, ImageSourcePropType> = {
  'best-ball': getAssetImageSource('images/games/best-ball-select.png', 'game-select', MEDIA_PAGE_PATH),
  '3-putts': getAssetImageSource('images/games/3-putts-select.png', 'game-select', MEDIA_PAGE_PATH),
  bunker: getAssetImageSource('images/games/bunker-select.png', 'game-select', MEDIA_PAGE_PATH),
  'high-low': getAssetImageSource('images/games/high-low-select.png', 'game-select', MEDIA_PAGE_PATH),
  mystery: getAssetImageSource('images/games/mystery-select.png', 'game-select', MEDIA_PAGE_PATH),
  penalty: getAssetImageSource('images/games/penalty-select.png', 'game-select', MEDIA_PAGE_PATH),
  skins: getAssetImageSource('images/games/skins-select.png', 'game-select', MEDIA_PAGE_PATH),
  stableford: getAssetImageSource('images/games/stableford-select.png', 'game-select', MEDIA_PAGE_PATH),
  'stroke-play': getAssetImageSource('images/games/stableford-select.png', 'game-select', MEDIA_PAGE_PATH),
  water: getAssetImageSource('images/games/water-select.png', 'game-select', MEDIA_PAGE_PATH),
};

function getGameSlug(name?: string | null) {
  const fromName = (name ?? '').trim();
  if (!fromName) {
    return '';
  }
  return fromName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getGameImageSource(name?: string | null): ImageSourcePropType {
  const slug = getGameSlug(name);
  if (!slug) return getAssetImageSource('images/games/mystery-select.png', 'game-select', MEDIA_PAGE_PATH);
  return GAME_IMAGE_MAP[slug] ?? getAssetImageSource('images/games/mystery-select.png', 'game-select', MEDIA_PAGE_PATH);
}

function resolveImageUriFromUnstableAssetPath(raw: string): string | null {
  try {
    const parsed = new URL(raw, 'http://localhost');
    if (!parsed.pathname.startsWith('/assets/')) {
      return null;
    }

    const unstablePath = parsed.searchParams.get('unstable_path');
    if (!unstablePath) {
      return null;
    }

    let normalized = decodeURIComponent(unstablePath).trim().replace(/\\/g, '/');
    normalized = normalized.replace(/^[./]+/, '');

    if (normalized.startsWith('assets-generated/images/') || normalized.startsWith('images/')) {
      return buildMediaUrl('images', normalized, 'game-select', { pagePath: MEDIA_PAGE_PATH });
    }
  } catch {
    return null;
  }

  return null;
}

function resolveGameImageUri(imagePath: string | undefined | null): string | null {
  const raw = (imagePath ?? '').trim();
  if (!raw) return null;

  const fromUnstableAssetPath = resolveImageUriFromUnstableAssetPath(raw);
  if (fromUnstableAssetPath) {
    return fromUnstableAssetPath;
  }

  if (raw.startsWith('http://') || raw.startsWith('https://')) {
    return raw;
  }

  if (raw.startsWith('/assets/')) {
    return null;
  }

  const normalized = raw.replace(/^\/+/, '');

  if (normalized.startsWith('assets-generated/images/') || normalized.startsWith('images/')) {
    return buildMediaUrl('images', normalized, 'game-select', { pagePath: MEDIA_PAGE_PATH });
  }

  if (raw.startsWith('s3://')) {
    const withoutScheme = raw.slice('s3://'.length);
    const slashIndex = withoutScheme.indexOf('/');
    if (slashIndex >= 0 && slashIndex + 1 < withoutScheme.length) {
      const key = withoutScheme.slice(slashIndex + 1).replace(/^\/+/, '');
      if (key.startsWith('assets-generated/images/') || key.startsWith('images/')) {
        return buildMediaUrl('images', key, 'game-select', { pagePath: MEDIA_PAGE_PATH });
      }
    }
  }

  return null;
}

export function resolveGameImageSource(
  imagePath: string | undefined | null,
  name?: string | null,
): ImageSourcePropType {
  const uri = resolveGameImageUri(imagePath);
  if (uri) {
    return { uri };
  }
  return getGameImageSource(name);
}
